import { Formik } from "formik";
import { useCallback, useEffect, useState } from "react";
import { FiFileText } from "react-icons/fi";
import { toast } from "sonner";
import { FormField } from "@/components/common/form-field";
import { DateField } from "@/components/common/masked-form-field";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckboxField, SelectField } from "@/features/clients/components/record-form-fields";
import type { Client } from "@/features/clients/types";
import {
  deleteDependent,
  deleteEmployer,
  getWorksheet,
  listDependents,
  listEmployers,
  otherBusinessesApi,
  patchClientMarital,
  patchWorksheet,
} from "@/features/worksheets/api";
import { AutosaveForm } from "@/features/worksheets/components/autosave-form";
import { CollectionCard, type Column } from "@/features/worksheets/components/collection-card";
import { DependentDialog } from "@/features/worksheets/components/dialogs/dependent-dialog";
import { EmployerDialog } from "@/features/worksheets/components/dialogs/employer-dialog";
import { OtherBusinessDialog } from "@/features/worksheets/components/dialogs/other-business-dialog";
import { PayStubsDialog } from "@/features/worksheets/components/dialogs/paystubs-dialog";
import { useCollection } from "@/features/worksheets/hooks/use-collection";
import {
  SWITCH_OPTIONS,
  type Dependent,
  type Employer,
  type OtherBusiness,
  type Switch,
} from "@/features/worksheets/types";

const MARITAL_OPTIONS = [
  { value: "MARRIED", label: "Married" },
  { value: "UNMARRIED", label: "Unmarried" },
];

type Gate = "dependentSw" | "employedSw" | "spEmployedSw" | "otherBusSw";

/** A yes/no gate select that persists to the worksheet header on change. */
function GateSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: Switch;
  onChange: (v: Switch) => void;
}) {
  return (
    <div className="grid gap-2">
      <Label className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
        {label}
      </Label>
      <Select value={value} onValueChange={(v) => onChange(v as Switch)}>
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SWITCH_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

interface Props {
  caseId: string;
  clientId: string;
  client: Client;
}

export function PersonalTab({ caseId, clientId, client }: Props) {
  const [gates, setGates] = useState<Record<Gate, Switch>>({
    dependentSw: "U",
    employedSw: "U",
    spEmployedSw: "U",
    otherBusSw: "U",
  });

  useEffect(() => {
    let active = true;
    getWorksheet(caseId)
      .then((ws) => {
        if (!active) return;
        setGates({
          dependentSw: ws.dependentSw,
          employedSw: ws.employedSw,
          spEmployedSw: ws.spEmployedSw,
          otherBusSw: ws.otherBusSw,
        });
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [caseId]);

  function setGate(field: Gate, value: Switch) {
    setGates((g) => ({ ...g, [field]: value }));
    patchWorksheet(caseId, { [field]: value }).catch(() => toast.error("Couldn't save."));
  }

  return (
    <div className="grid gap-6">
      {/* Marital status — lives on the Client, edited here and reused everywhere. */}
      <Card className="p-4">
        <h3 className="mb-3 font-semibold">Marital status</h3>
        <Formik
          initialValues={{
            maritalStatus: client.maritalStatus ?? "",
            dateOfMarriage: client.dateOfMarriage ?? "",
            inCommunityPropertyState: client.inCommunityPropertyState ?? false,
          }}
          onSubmit={() => undefined}
          enableReinitialize
        >
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <SelectField name="maritalStatus" label="Marital status" options={MARITAL_OPTIONS} />
              <DateField name="dateOfMarriage" label="Date of marriage" />
            </div>
            <CheckboxField
              name="inCommunityPropertyState"
              label="Lives in a community-property state"
            />
            <AutosaveForm save={(patch) => patchClientMarital(clientId, patch)} />
          </div>
        </Formik>
      </Card>

      {/* Section gates */}
      <Card className="p-4">
        <h3 className="mb-3 font-semibold">Household & employment</h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <GateSelect label="Has dependents?" value={gates.dependentSw} onChange={(v) => setGate("dependentSw", v)} />
          <GateSelect label="Taxpayer employed?" value={gates.employedSw} onChange={(v) => setGate("employedSw", v)} />
          <GateSelect label="Spouse employed?" value={gates.spEmployedSw} onChange={(v) => setGate("spEmployedSw", v)} />
          <GateSelect label="Other business interests?" value={gates.otherBusSw} onChange={(v) => setGate("otherBusSw", v)} />
        </div>
      </Card>

      {gates.dependentSw === "Y" && <DependentsSection clientId={clientId} />}
      {gates.employedSw === "Y" && (
        <EmployersSection clientId={clientId} belongsTo="TAXPAYER" title="Taxpayer employers" />
      )}
      {gates.spEmployedSw === "Y" && (
        <EmployersSection clientId={clientId} belongsTo="SPOUSE" title="Spouse employers" />
      )}
      {gates.otherBusSw === "Y" && <OtherBusinessSection caseId={caseId} />}
    </div>
  );
}

// --- dependents --------------------------------------------------------------

function DependentsSection({ clientId }: { clientId: string }) {
  const list = useCallback((id: string) => listDependents<Dependent>(id), []);
  const { rows, loading, reload } = useCollection<Dependent>(clientId, list);
  const [dialog, setDialog] = useState<{ open: boolean; record: Dependent | null }>({
    open: false,
    record: null,
  });

  const columns: Column<Dependent>[] = [
    { header: "Name", cell: (r) => r.name },
    { header: "SSN", cell: (r) => r.ssnLabel || "—" },
    { header: "Age", cell: (r) => r.age ?? "—" },
    { header: "Relationship", cell: (r) => r.relationship || "—" },
  ];

  return (
    <>
      <CollectionCard
        title="Dependents"
        addLabel="Add Dependent"
        columns={columns}
        rows={rows}
        loading={loading}
        onAdd={() => setDialog({ open: true, record: null })}
        onEdit={(record) => setDialog({ open: true, record })}
        onDelete={(row) => deleteDependent(clientId, row.id)}
      />
      <DependentDialog
        clientId={clientId}
        record={dialog.record}
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        onSaved={reload}
      />
    </>
  );
}

// --- employers (+ pay stubs) -------------------------------------------------

function EmployersSection({
  clientId,
  belongsTo,
  title,
}: {
  clientId: string;
  belongsTo: "TAXPAYER" | "SPOUSE";
  title: string;
}) {
  const list = useCallback((id: string) => listEmployers<Employer>(id), []);
  const { rows, loading, reload } = useCollection<Employer>(clientId, list);
  const filtered = rows.filter((r) => r.belongsTo === belongsTo);
  const [dialog, setDialog] = useState<{ open: boolean; record: Employer | null }>({
    open: false,
    record: null,
  });
  const [payStubs, setPayStubs] = useState<{ open: boolean; employer: Employer | null }>({
    open: false,
    employer: null,
  });

  const columns: Column<Employer>[] = [
    { header: "Employer", cell: (r) => r.name },
    { header: "Occupation", cell: (r) => r.occupation || "—" },
    { header: "Pay period", cell: (r) => r.payPeriod || "—" },
    { header: "Gross wages", cell: (r) => (r.grossWages ? `$${r.grossWages}` : "—") },
    {
      header: "",
      cell: (r) => (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => setPayStubs({ open: true, employer: r })}
        >
          <FiFileText className="size-4" /> Pay stubs
        </Button>
      ),
    },
  ];

  return (
    <>
      <CollectionCard
        title={title}
        addLabel="Add Employer"
        columns={columns}
        rows={filtered}
        loading={loading}
        onAdd={() => setDialog({ open: true, record: null })}
        onEdit={(record) => setDialog({ open: true, record })}
        onDelete={(row) => deleteEmployer(clientId, row.id)}
      />
      <EmployerDialog
        clientId={clientId}
        belongsTo={belongsTo}
        record={dialog.record}
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        onSaved={reload}
      />
      <PayStubsDialog
        clientId={clientId}
        employer={payStubs.employer}
        open={payStubs.open}
        onOpenChange={(open) => setPayStubs((s) => ({ ...s, open }))}
      />
    </>
  );
}

// --- other business interests ------------------------------------------------

function OtherBusinessSection({ caseId }: { caseId: string }) {
  const { rows, loading, reload } = useCollection<OtherBusiness>(caseId, otherBusinessesApi.list);
  const [dialog, setDialog] = useState<{ open: boolean; record: OtherBusiness | null }>({
    open: false,
    record: null,
  });

  const columns: Column<OtherBusiness>[] = [
    { header: "Business", cell: (r) => r.name },
    { header: "Ownership %", cell: (r) => `${r.percent}%` },
    { header: "Title", cell: (r) => r.title || "—" },
    { header: "EIN", cell: (r) => r.einLabel || "—" },
  ];

  return (
    <>
      <CollectionCard
        title="Other business interests"
        addLabel="Add Business"
        columns={columns}
        rows={rows}
        loading={loading}
        onAdd={() => setDialog({ open: true, record: null })}
        onEdit={(record) => setDialog({ open: true, record })}
        onDelete={(row) => otherBusinessesApi.remove(caseId, row.id)}
      />
      <OtherBusinessDialog
        caseId={caseId}
        record={dialog.record}
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        onSaved={reload}
      />
    </>
  );
}
